import {
  isDefiniteNewVehicleRejection,
  newVehicleApi,
  useCreateMakeMutation,
  useCreateModelMutation,
  useUpdateMakeMutation,
  useUpdateModelMutation,
  useCreateModelYearMutation,
  useCreateEngineMutation,
  useCreateTrimMutation,
  useCreateVariantMutation,
  useCreateColourMutation,
  usePutColourTrimsMutation,
  useCreateUpholsteryMutation,
  usePutTrimUpholsteryMutation,
  usePutColourUpholsteryRestrictionsMutation,
  useCreateFeatureMutation,
  usePutTrimFeatureAssignmentsMutation,
  useCreatePackageMutation,
} from '@dms/api/new-vehicle';

import {resolveMakeIdentity, resolveModelIdentity} from '../utils/catalogueIdentity';
import {
  catalogueCodeConflict,
  catalogueErrorCodes,
  catalogueErrorMessage,
  engineHybridType,
  t,
  VEHICLE_CATALOGUE_UNAVAILABLE,
} from '../utils/catalogueUi';
import {equipmentWriteBody} from '../utils/equipmentDraft';
import {
  codedDraftRows,
  reviewNewModelDraft,
  variantKey,
  type Materialization,
  type NewModelDraft,
} from '../utils/newModelDraft';

const codeConflictOf = (draft: NewModelDraft, rowId: string, error: unknown) => {
  const message = catalogueCodeConflict(error);
  const row = codedDraftRows(draft).find(({key}) => key === rowId);
  return message && row ? {rowId, code: row.code, message} : null;
};
const isNotFound = (error: unknown) =>
  typeof error === 'object' && error !== null && 'status' in error && error.status === 404;
/** The backend checks catalogue keys before writing, so a catalogue outage created nothing. */
const isCatalogueUnavailable = (error: unknown) =>
  typeof error === 'object' &&
  error !== null &&
  'status' in error &&
  error.status === 503 &&
  catalogueErrorCodes(error).includes(VEHICLE_CATALOGUE_UNAVAILABLE);
const pendingLabel = (draft: NewModelDraft, key: string) => {
  if (key === 'make') {
    return t('entity.vehicle.labels.make', 'make');
  }
  if (key === 'model') {
    return t('entity.vehicle.labels.model', 'model');
  }
  if (key === 'model-year') {
    return t('entity.newVehicle.labels.modelYear', 'model year');
  }
  const row = [
    ...draft.engines,
    ...draft.trims,
    ...draft.colours,
    ...draft.upholsteries,
    ...draft.equipment,
    ...draft.packages,
  ].find(({id}) => id === key);
  return row?.name || t('entity.newVehicle.authoring.catalogueRecord', 'catalogue record');
};
/** 404 → null (checkpoint is stale); anything else rethrows so a transient failure never drops it. */
const lookup = async <T>(get: () => Promise<T>) => {
  try {
    return await get();
  } catch (error) {
    if (isNotFound(error)) {
      return null;
    }
    throw error;
  }
};
export type StaleCheckpointLevel = 'make' | 'model' | 'model-year';

/**
 * Dependency ordering only: all transport and request types remain in the generated C1 hooks.
 * Every successful write is checkpointed into `materialization.created` so a failed run resumes
 * without re-creating entities; replace-style PUTs are idempotent and simply re-run.
 */
// eslint-disable-next-line max-lines-per-function -- Pre-existing violation
export function useMaterializeNewModel() {
  const [createMake] = useCreateMakeMutation();
  const [createModel] = useCreateModelMutation();
  const [updateMake] = useUpdateMakeMutation();
  const [updateModel] = useUpdateModelMutation();
  const [createYear] = useCreateModelYearMutation();
  const [createEngine] = useCreateEngineMutation();
  const [createTrim] = useCreateTrimMutation();
  const [createVariant] = useCreateVariantMutation();
  const [createColour] = useCreateColourMutation();
  const [putColourTrims] = usePutColourTrimsMutation();
  const [createUpholstery] = useCreateUpholsteryMutation();
  const [putTrimUpholstery] = usePutTrimUpholsteryMutation();
  const [putRestrictions] = usePutColourUpholsteryRestrictionsMutation();
  const [getRestrictions] = newVehicleApi.useLazyGetColourUpholsteryRestrictionsQuery();
  const [createFeature] = useCreateFeatureMutation();
  const [putFeatures] = usePutTrimFeatureAssignmentsMutation();
  const [getTrim] = newVehicleApi.useLazyGetTrimQuery();
  const [getColour] = newVehicleApi.useLazyGetColourQuery();
  const [getMake] = newVehicleApi.useLazyGetMakeQuery();
  const [getModel] = newVehicleApi.useLazyGetModelQuery();
  const [getModelYear] = newVehicleApi.useLazyGetModelYearQuery();
  const [listMakes] = newVehicleApi.useLazyListMakeQuery();
  const [listModels] = newVehicleApi.useLazyListModelQuery();
  const [createPackage] = useCreatePackageMutation();

  // eslint-disable-next-line complexity, max-lines-per-function, max-statements -- Pre-existing violation
  return async (
    draft: NewModelDraft,
    canViewCost: boolean,
    checkpoint: (state: Materialization) => void,
    onStaleCheckpoint?: (level: StaleCheckpointLevel) => void
  ) => {
    if (reviewNewModelDraft(draft).length || draft.materialization.status === 'complete') {
      throw new Error('Draft has unresolved issues or is already materialized.');
    }
    let state: Materialization = {
      ...draft.materialization,
      status: 'started',
      created: {...draft.materialization.created},
      lastError: null,
      codeConflict: null,
    };
    let checkpointFailed = false;
    const commit = (patch: Partial<Materialization>) => {
      state = {...state, ...patch, created: {...state.created, ...patch.created}};
      try {
        checkpoint(state);
      } catch (error) {
        checkpointFailed = true;
        throw error;
      }
    };
    const createPending = async (
      key: string,
      create: () => Promise<{id: string}>,
      completed: (id: string) => Partial<Materialization>
    ) => {
      commit({pendingCreate: key});
      try {
        const {id} = await create();
        commit({...completed(id), pendingCreate: null});
        return id;
      } catch (error) {
        if (isDefiniteNewVehicleRejection(error) || isCatalogueUnavailable(error)) {
          commit({pendingCreate: null, codeConflict: codeConflictOf(draft, key, error)});
        }
        throw error;
      }
    };
    const once = (rowId: string, create: () => Promise<{id: string}>) => {
      const known = state.created[rowId];
      if (known) {
        return Promise.resolve(known);
      }
      return createPending(rowId, create, (id) => ({created: {[rowId]: id}}));
    };
    const resolve = (rowId: string) => {
      const value = state.created[rowId];
      if (!value) {
        throw new Error(`Unresolved draft reference: ${rowId}`);
      }
      return value;
    };
    const priceCost = (value: string) => (canViewCost ? {costNet: value || null} : {});
    const offeredTrims = (
      trimIds: string[],
      row: {trimPrices: Record<string, string>; costNet: string}
    ) =>
      trimIds.map((trimId) => ({
        trimId: resolve(trimId),
        priceNet: row.trimPrices[trimId] ?? '',
        ...priceCost(row.costNet),
      }));
    commit({});
    try {
      if (state.pendingCreate) {
        throw new Error(
          t(
            'entity.newVehicle.authoring.createOutcomeUnconfirmed',
            'The outcome of creating {thing} could not be confirmed. Check the catalogue. If it was not created, use Start over; if it was created, continue there.',
            {thing: pendingLabel(draft, state.pendingCreate)}
          )
        );
      }
      // Checkpointed parents may have been archived, deleted or (legacy drafts) never recorded;
      // the first invalid level drops itself and everything below so nothing is posted onto it.
      if (state.makeId || state.modelId || state.modelYearId) {
        let stale: StaleCheckpointLevel | null = null;
        let parentMakeId = draft.make.mode === 'existing' ? draft.make.id : state.makeId;
        const makeId = parentMakeId;
        if (draft.make.mode === 'new' && makeId) {
          const make = await lookup(() => getMake({makeId}, false).unwrap());
          // eslint-disable-next-line max-depth -- Pre-existing violation
          if (!make || make.archived) {
            stale = 'make';
            parentMakeId = null;
          }
        }
        let parentModelId = draft.model.mode === 'existing' ? draft.model.id : state.modelId;
        const modelId = parentModelId;
        if (draft.model.mode === 'new' && modelId) {
          const model = parentMakeId
            ? await lookup(() => getModel({modelId}, false).unwrap())
            : null;
          // eslint-disable-next-line max-depth -- Pre-existing violation
          if (!model || model.archived || model.makeId !== parentMakeId) {
            stale ??= 'model';
            parentModelId = null;
          }
        }
        const modelYearId = state.modelYearId;
        if (modelYearId) {
          const year = parentModelId
            ? await lookup(() => getModelYear({modelYearId}, false).unwrap())
            : null;
          // eslint-disable-next-line max-depth -- Pre-existing violation
          if (!year || year.status === 'archived' || year.modelId !== parentModelId) {
            stale ??= 'model-year';
          }
        }
        if (stale) {
          const equipmentRows = new Set(draft.equipment.map((row) => row.id));
          state = {
            ...state,
            makeId: stale === 'make' ? null : state.makeId,
            modelId: stale === 'model-year' ? state.modelId : null,
            modelYearId: null,
            created: Object.fromEntries(
              Object.entries(state.created).filter(([rowId]) => equipmentRows.has(rowId))
            ),
          };
          commit({});
          onStaleCheckpoint?.(stale);
        }
      }
      // Identity against the live catalogue, since the draft may be older than a make or model
      // another author added. An exact match is used (and linked when it has no key yet) instead
      // of posting a duplicate the backend would refuse; the used id is checkpointed like a
      // created one so a retry keeps it. Look-alikes were settled by the author in step 1/2.
      // Links are minimal PATCHes: fields left out stay as they are.
      const linkMake = async (make: {id: string; name: string; version: number}, key: string) => {
        await updateMake({
          makeId: make.id,
          makeWriteRequestBody: {name: make.name, vehicleDataKey: key, version: make.version},
        }).unwrap();
      };
      const linkModel = async (
        model: {id: string; name: string; makeId: string; version: number},
        key: string
      ) => {
        await updateModel({
          modelId: model.id,
          modelWriteRequestBody: {
            makeId: model.makeId,
            name: model.name,
            vehicleDataKey: key,
            version: model.version,
          },
        }).unwrap();
      };
      const resolveMake = async () => {
        const {items} = await listMakes({}, false).unwrap();
        if (draft.make.mode === 'existing') {
          const make = items.find(({id}) => id === draft.make.id);
          if (make && draft.make.vehicleDataKey && !make.vehicleDataKey) {
            await linkMake(make, draft.make.vehicleDataKey);
          }
          return draft.make.id;
        }
        const {match} = resolveMakeIdentity(items, draft.make);
        if (match && match.kind !== 'archived') {
          if (match.kind === 'link') {
            await linkMake(match.record, draft.make.vehicleDataKey);
          }
          commit({makeId: match.record.id});
          return match.record.id;
        }
        return createPending(
          'make',
          () =>
            createMake({
              makeWriteRequestBody: {
                name: draft.make.name.trim(),
                vehicleDataKey: draft.make.vehicleDataKey || null,
                hsn: draft.make.hsn || null,
                version: 1,
              },
            }).unwrap(),
          (id) => ({makeId: id})
        );
      };
      const makeId = draft.make.mode === 'new' && state.makeId ? state.makeId : await resolveMake();
      const resolveModel = async () => {
        const {items} = await listModels({}, false).unwrap();
        if (draft.model.mode === 'existing') {
          const model = items.find(({id}) => id === draft.model.id);
          if (model && draft.model.vehicleDataKey && !model.vehicleDataKey) {
            await linkModel(model, draft.model.vehicleDataKey);
          }
          return draft.model.id;
        }
        const {match} = resolveModelIdentity(items, makeId, draft.model);
        if (match && match.kind !== 'archived') {
          if (match.kind === 'link') {
            await linkModel(match.record, draft.model.vehicleDataKey);
          }
          commit({modelId: match.record.id});
          return match.record.id;
        }
        return createPending(
          'model',
          () =>
            createModel({
              modelWriteRequestBody: {
                makeId,
                name: draft.model.name.trim(),
                vehicleDataKey: draft.model.vehicleDataKey || null,
                modelFamilyGroup: null,
                bodyStyle: null,
                modelFamily: draft.model.modelFamily || null,
                tsn: draft.model.tsn || null,
                version: 1,
              },
            }).unwrap(),
          (id) => ({modelId: id})
        );
      };
      const modelId =
        draft.model.mode === 'new' && state.modelId ? state.modelId : await resolveModel();
      const modelYearId =
        state.modelYearId ??
        (await createPending(
          'model-year',
          () =>
            createYear({
              modelYearWriteRequestBody: {
                modelId,
                name: draft.year.code,
                catalogueName: draft.year.catalogueName,
                vatRateType: 'S',
                validFrom: draft.year.validFrom ?? '',
                validTo: draft.year.validTo || null,
                status: 'draft',
                pricelistVersionLabel: draft.year.pricelistVersion,
                version: 1,
              },
            }).unwrap(),
          (id) => ({modelYearId: id})
        ));

      for (const row of draft.engines) {
        await once(row.id, () =>
          createEngine({
            engineWriteRequestBody: {
              modelYearId,
              name: row.name,
              codes: row.factoryCode ? [row.factoryCode] : [],
              powerKw: row.powerKw,
              powerPs: row.powerPs || null,
              displacementCcm: row.displacementCcm ? Number(row.displacementCcm) : null,
              factoryCode: row.factoryCode || null,
              transmissionCode: row.transmissionCode || null,
              fuel: row.fuel,
              hybridType: engineHybridType(row),
              transmission: row.transmission,
              drive: row.drive,
              version: 1,
            },
          }).unwrap()
        );
      }
      for (const row of draft.trims) {
        await once(row.id, () =>
          createTrim({
            trimWriteRequestBody: {
              modelYearId,
              name: row.name,
              bodyStyle: row.bodyStyle,
              codes: row.factoryCode ? [row.factoryCode] : [],
              type: row.type || null,
              basePriceNet: row.basePriceNet,
              ...priceCost(row.costNet),
              publicationStatus: 'draft',
              commercialAvailability: 'preview',
              orderableFrom: row.validFrom || null,
              orderableUntil: row.validTo || null,
              version: 1,
            },
          }).unwrap()
        );
      }
      for (const row of draft.variants) {
        await once(variantKey(row.trimId, row.engineId), () =>
          createVariant({
            variantItemWriteRequestBody: {
              trimId: resolve(row.trimId),
              engineId: resolve(row.engineId),
              priceNet: row.priceNet,
              ...priceCost(row.costNet),
              orderCode: row.orderCode,
              enabled: true,
              roofHeight: null,
              loadLength: null,
              gvwKg: null,
              payloadKg: null,
              reviewFlags: [],
              version: 1,
            },
          }).unwrap()
        );
      }
      for (const row of draft.colours) {
        const colourId = await once(row.id, () =>
          createColour({
            colourWriteRequestBody: {
              modelYearId,
              name: row.name,
              codes: row.factoryCode ? [row.factoryCode] : [],
              kind: row.kind || null,
              hex: row.hex || null,
              factoryCode: row.factoryCode || null,
              version: 1,
            },
          }).unwrap()
        );
        const colour = await getColour({colourId}, false).unwrap();
        await putColourTrims({
          colourId,
          replaceColourTrimsRequestBody: {
            trims: offeredTrims(row.trimIds, row),
            version: colour.version,
          },
        }).unwrap();
      }
      for (const row of draft.upholsteries) {
        await once(row.id, () =>
          createUpholstery({
            upholsteryWriteRequestBody: {
              modelYearId,
              name: row.name,
              codes: row.factoryCode ? [row.factoryCode] : [],
              fabric: row.fabric || null,
              interiorColorHex: row.hex || null,
              factoryCode: row.factoryCode || null,
              priceNet: row.priceNet,
              ...priceCost(row.costNet),
              version: 1,
            },
          }).unwrap()
        );
      }
      for (const row of draft.equipment) {
        if (row.libraryId) {
          // eslint-disable-next-line max-depth -- Pre-existing violation
          if (!state.created[row.id]) {
            commit({created: {[row.id]: row.libraryId}});
          }
        } else {
          await once(row.id, () =>
            createFeature({
              featureWriteRequestBody: equipmentWriteBody(row),
            }).unwrap()
          );
        }
      }
      for (const row of draft.trims) {
        const trimId = resolve(row.id);
        const trim = await getTrim({trimId}, false).unwrap();
        await putTrimUpholstery({
          trimId,
          replaceTrimUpholsteryRequestBody: {
            upholsteryIds: draft.upholsteries
              .filter((u) => u.trimIds.includes(row.id))
              .map((u) => resolve(u.id)),
            version: trim.version,
          },
        }).unwrap();
        const refreshedTrim = await getTrim({trimId}, false).unwrap();
        await putFeatures({
          trimId,
          replaceTrimFeatureAssignmentsRequestBody: {
            version: refreshedTrim.version,
            assignments: draft.attachments
              .filter((a) => a.trimId === row.id)
              .map((a, i) => {
                const equipment = draft.equipment.find(({id}) => id === a.featureId);
                return {
                  featureId: resolve(a.featureId),
                  inclusion: a.inclusion,
                  appliesIf: {anyOfVariantIds: [], allOfFeatureIds: []},
                  priceIf: [],
                  variantPriceOverrides: [],
                  priceNet: a.inclusion === 'OPTIONAL' ? a.priceNet : null,
                  ...priceCost(equipment?.costNet ?? ''),
                  sortOrder: i,
                };
              }),
          },
        }).unwrap();
      }
      for (const row of draft.packages) {
        await once(row.id, () =>
          createPackage({
            packageWriteRequestBody: {
              modelYearId,
              name: row.name,
              codes: row.factoryCode ? [row.factoryCode] : [],
              description: row.description,
              memberFeatureIds: row.memberFeatureIds.map(resolve),
              trims: offeredTrims(row.applicableTrimIds, row),
              status: 'draft',
              version: 1,
            },
          }).unwrap()
        );
      }
      if (draft.restrictions.length > 0) {
        const current = await getRestrictions({modelYearId}, false).unwrap();
        await putRestrictions({
          modelYearId,
          replaceColourUpholsteryRestrictionsRequestBody: {
            version: current.version,
            restrictions: draft.restrictions.map(({colourId, upholsteryId}) => ({
              colourId: resolve(colourId),
              upholsteryId: resolve(upholsteryId),
            })),
          },
        }).unwrap();
      }
      commit({status: 'complete'});
      return {modelYearId};
    } catch (error) {
      if (!checkpointFailed) {
        try {
          commit({lastError: catalogueErrorMessage(error)});
        } catch {
          // Storage failed while recording the error; the original failure is what matters.
        }
      }
      throw error;
    }
  };
}
